import { useEffect, useState } from 'react';
import { useThemeUI } from 'theme-ui';

import type { MediaQueryAllQueryable } from '../types/mediaQuery';

export interface MediaQuerySettings extends MediaQueryAllQueryable {
  query?: string;
}

export interface MediaQueryDevice {
  width?: number | string;
}

interface ResolvedMediaQuerySettings {
  maxWidth?: number;
  minWidth?: number;
  query?: string;
}

export const useMediaQuery = (
  settings: Partial<MediaQuerySettings>,
  device?: MediaQueryDevice,
) => {
  const resolvedSettings = useMediaQuerySettings(settings);
  const query = toMediaQuery(resolvedSettings);
  const deviceMatch = getDeviceMatch(resolvedSettings, device);

  const [matches, setMatches] = useState(() =>
    getInitialMatch(query, deviceMatch),
  );

  useEffect(() => {
    if (deviceMatch !== undefined) {
      setMatches(deviceMatch);
      return undefined;
    }

    if (!query || typeof window === 'undefined' || !window.matchMedia) {
      setMatches(false);
      return undefined;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', handleChange);

    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }, [deviceMatch, query]);

  return matches;
};

export const useMediaQuerySettings = (
  settings: Partial<MediaQuerySettings>,
): ResolvedMediaQuerySettings => {
  const { theme } = useThemeUI();

  const getValue = (key: keyof typeof settings): number | undefined => {
    const value = settings[key];

    if (Array.isArray(value)) {
      const [breakpoint, offset] = value;
      // @ts-ignore Theme UI
      return Number.parseInt(theme.mediaQueries[breakpoint], 10) + offset;
    }

    return typeof value === 'string'
      ? // @ts-ignore Theme UI
        Number.parseInt(theme.mediaQueries[value], 10)
      : value;
  };

  return {
    ...settings,
    maxWidth: getValue('maxWidth'),
    minWidth: getValue('minWidth'),
  };
};

const toMediaQuery = (settings: ResolvedMediaQuerySettings) => {
  const queries: string[] = [];

  if (settings.query) {
    queries.push(settings.query);
  }

  if (settings.minWidth !== undefined) {
    queries.push(`(min-width: ${settings.minWidth}px)`);
  }

  if (settings.maxWidth !== undefined) {
    queries.push(`(max-width: ${settings.maxWidth}px)`);
  }

  return queries.join(' and ');
};

const getInitialMatch = (query: string, deviceMatch: boolean | undefined) => {
  if (deviceMatch !== undefined) {
    return deviceMatch;
  }

  if (!query || typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }

  return window.matchMedia(query).matches;
};

const getDeviceMatch = (
  settings: ResolvedMediaQuerySettings,
  device?: MediaQueryDevice,
) => {
  if (!device) {
    return undefined;
  }

  const width = getDeviceDimension(device.width);

  if (settings.minWidth !== undefined) {
    if (width === undefined || width < settings.minWidth) {
      return false;
    }
  }

  if (settings.maxWidth !== undefined) {
    if (width === undefined || width > settings.maxWidth) {
      return false;
    }
  }

  return true;
};

const getDeviceDimension = (value: number | string | undefined) => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    return Number.parseInt(value, 10);
  }

  return undefined;
};
